import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { generateQuotationPDF, downloadQuotationPDF } from '../utils/generateQuotationPDF';
import { Routes, Route, useNavigate, useParams, Link } from 'react-router-dom';
import {
  FiPlus, FiSearch, FiEdit2, FiMessageCircle, FiCheckCircle, FiX,
  FiMail, FiArrowLeft, FiTrash2, FiChevronRight, FiClock, FiPhone,
  FiPrinter, FiCopy, FiUser, FiBriefcase, FiCalendar,
  FiPackage
} from 'react-icons/fi';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { useStaffLog } from '../utils/useStaffLog';
import './AdminCRM.css';

/* ────────────────────────────────────────────────────────────────────
   Helpers
   ──────────────────────────────────────────────────────────────────── */

const STATUS_META = {
  draft:            { label: 'Draft',         tone: 'gray'   },
  sent:             { label: 'Sent',          tone: 'blue'   },
  negotiation:      { label: 'Negotiating',   tone: 'amber'  },
  confirmed:        { label: 'Confirmed',     tone: 'green'  },
  payment_partial:  { label: 'Partial Paid',  tone: 'amber'  },
  payment_done:     { label: 'Paid',          tone: 'green'  },
  printing:         { label: 'Printing',      tone: 'blue'   },
  ready:            { label: 'Ready',         tone: 'blue'   },
  out_for_delivery: { label: 'Out for Delivery', tone: 'blue' },
  delivered:        { label: 'Delivered',     tone: 'green'  },
};

const OUTCOME_META = {
  interested:    { label: 'Interested',     tone: 'green' },
  needs_time:    { label: 'Needs Time',     tone: 'amber' },
  not_reachable: { label: 'Not Reachable',  tone: 'gray'  },
  rejected:      { label: 'Rejected',       tone: 'red'   },
  converted:     { label: 'Converted',      tone: 'green' },
};

const CHANNEL_LABELS = {
  whatsapp: 'WhatsApp',
  call:     'Call',
  email:    'Email',
  visit:    'Visit',
};


const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, tone: 'gray' };
  return <span className={`crm-badge crm-badge--${meta.tone}`}>{meta.label}</span>;
};

const OutcomeBadge = ({ outcome }) => {
  const meta = OUTCOME_META[outcome] || { label: outcome, tone: 'gray' };
  return <span className={`crm-badge crm-badge--${meta.tone}`}>{meta.label}</span>;
};

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const lineTotal = (p) => {
  const qty = Number(p.qty) || 0;
  const rate = Number(p.rate) || 0;
  const gst = Number(p.gstPercent) || 0;
  const base = qty * rate;
  return base + (base * gst / 100);
};

const grandTotalOf = (products) => (products || []).reduce((sum, p) => sum + lineTotal(p), 0);

const emptyProduct = () => ({
  name: '', description: '', qty: 1, rate: 0, gstPercent: 18,
});

const emptyCustomer = () => ({
  customerName: '', customerPhone: '', customerEmail: '',
  companyName: '', gstNo: '', companyAddress: '',
});

// Builds Q1 / Q1.1 / Q1.2 ... labels for a flat list of quotations
const buildChainLabels = (quotations) => {
  const groups = {};
  quotations.forEach(q => {
    const key = q.rootId || q.id;
    if (!groups[key]) groups[key] = [];
    groups[key].push(q);
  });
  const rootKeys = Object.keys(groups).sort((a, b) => {
    const aMin = Math.min(...groups[a].map(q => q.id));
    const bMin = Math.min(...groups[b].map(q => q.id));
    return aMin - bMin;
  });
  const labels = {};
  const orderedGroups = [];
  rootKeys.forEach((key, idx) => {
    const items = [...groups[key]].sort((a, b) => a.roundNumber - b.roundNumber);
    // Use rootId (= the root quotation's DB id) as the display number so
    // Q8 always shows as Q8 regardless of how many quotations are in context.
    const rootNum = Number(key);
    items.forEach(q => {
      labels[q.id] = q.roundNumber > 0 ? `Q${rootNum}.${q.roundNumber}` : `Q${rootNum}`;
    });
    orderedGroups.push({ rootIndex: idx + 1, rootNum, items });
  });
  return { labels, groups: orderedGroups };
};

const buildShareText = (q, label) => {
  const lines = [
    `Angel Printers — Quotation ${label}`,
    '',
    `To: ${q.customerName}${q.companyName ? ` (${q.companyName})` : ''}`,
    q.gstNo ? `GSTIN: ${q.gstNo}` : null,
    '',
    'Items:',
    ...(Array.isArray(q.products) ? q.products : []).map(p =>
      `• ${p.name}${p.description ? ` — ${p.description}` : ''} | Qty ${p.qty} x ${money(p.rate)} (GST ${p.gstPercent}%) = ${money(lineTotal(p))}`
    ),
    '',
    `Grand Total: ${money(q.grandTotal)}`,
    '',
    'Thank you for choosing Angel Printers!',
  ].filter(Boolean);
  return lines.join('\n');
};

/* ────────────────────────────────────────────────────────────────────
   Preview / Share popup (Confirm flow: preview, mail, whatsapp)
   ──────────────────────────────────────────────────────────────────── */

const PreviewModal = ({ quotation, label, onClose, onConfirm, confirming }) => {
  const shareText = useMemo(() => buildShareText(quotation, label), [quotation, label]);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [pdfBytes, setPdfBytes] = useState(null);

  // Generate PDF once on mount
  useEffect(() => {
    let cancelled = false;
    setGeneratingPdf(true);
    generateQuotationPDF(quotation, label)
      .then(bytes => { if (!cancelled) { setPdfBytes(bytes); setGeneratingPdf(false); } })
      .catch(() => { if (!cancelled) setGeneratingPdf(false); });
    return () => { cancelled = true; };
  }, [quotation, label]);

  const handleDownloadPdf = () => {
    if (!pdfBytes) return;
    downloadQuotationPDF(pdfBytes, label);
  };

  // Email: attach note + text body; PDF must be downloaded separately (mailto can't attach)
  const mailSubject = encodeURIComponent(`Angel Printers — Quotation ${label}`);
  const mailBody    = encodeURIComponent(shareText + '\n\n[Please find the PDF quotation attached — download it from the button above and attach to your email.]');
  const mailHref    = `mailto:${quotation.customerEmail || ''}?subject=${mailSubject}&body=${mailBody}`;

  // WhatsApp: text message + hint about PDF
  const waPhone = (quotation.customerPhone || '').replace(/\D/g, '');
  const waText  = shareText + '\n\n_(PDF copy available on request)_';
  const waHref  = `https://wa.me/${waPhone.length === 10 ? '91' + waPhone : waPhone}?text=${encodeURIComponent(waText)}`;

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success('Quotation text copied');
    } catch {
      toast.error('Could not copy — select & copy manually');
    }
  };

  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div className="crm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="crm-modal-head">
          <h3>Preview &amp; Share — {label}</h3>
          <button className="crm-icon-btn" onClick={onClose}><FiX size={18} /></button>
        </div>

        <div className="crm-quote-preview">
          <div className="crm-quote-preview-top">
            <div>
              <div className="crm-quote-brand">Angel Printers</div>
              <div className="crm-quote-meta-line">Quotation {label}</div>
            </div>
            <StatusBadge status={quotation.status} />
          </div>

          <div className="crm-quote-preview-parties">
            <div>
              <div className="crm-quote-label">Billed To</div>
              <div className="crm-quote-strong">{quotation.customerName}</div>
              {quotation.companyName && <div>{quotation.companyName}</div>}
              {quotation.gstNo && <div>GSTIN: {quotation.gstNo}</div>}
              {quotation.companyAddress && <div>{quotation.companyAddress}</div>}
              <div>{quotation.customerPhone}</div>
              {quotation.customerEmail && <div>{quotation.customerEmail}</div>}
            </div>
          </div>

          <table className="crm-quote-preview-table">
            <thead>
              <tr><th>Product</th><th>Qty</th><th>Rate</th><th>GST</th><th>Total</th></tr>
            </thead>
            <tbody>
              {(Array.isArray(quotation.products) ? quotation.products : []).map((p, i) => (
                <tr key={i}>
                  <td>
                    <div className="crm-quote-strong">{p.name}</div>
                    {p.description && <div className="crm-quote-muted">{p.description}</div>}
                  </td>
                  <td>{p.qty}</td>
                  <td>{money(p.rate)}</td>
                  <td>{p.gstPercent}%</td>
                  <td>{money(lineTotal(p))}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="crm-quote-preview-total">
            <span>Grand Total</span>
            <span>{money(quotation.grandTotal)}</span>
          </div>
        </div>

        {/* PDF download row */}
        <div className="crm-pdf-download-row">
          <button
            className="crm-btn crm-btn--pdf"
            onClick={handleDownloadPdf}
            disabled={generatingPdf || !pdfBytes}
            title="Download PDF"
          >
            <FiPrinter size={15} />
            {generatingPdf ? 'Generating PDF…' : 'Download PDF'}
          </button>
          <span className="crm-pdf-hint">
            Download the PDF then attach it to your email or share via WhatsApp.
          </span>
        </div>

        <div className="crm-modal-actions">
          <a className="crm-btn crm-btn--outline" href={mailHref}>
            <FiMail size={15} /> Email
          </a>
          <a className="crm-btn crm-btn--outline" href={waHref} target="_blank" rel="noreferrer">
            <FiMessageCircle size={15} /> WhatsApp
          </a>
          <button className="crm-btn crm-btn--outline" onClick={copyText}>
            <FiCopy size={15} /> Copy Text
          </button>
          {onConfirm && (
            <button className="crm-btn crm-btn--primary" onClick={onConfirm} disabled={confirming}>
              <FiCheckCircle size={15} /> {confirming ? 'Confirming…' : 'Mark as Confirmed'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
   Product table (editable)
   ──────────────────────────────────────────────────────────────────── */

const ProductTable = ({ products, setProducts }) => {
  const update = (idx, field, value) => {
    const next = [...products];
    next[idx] = { ...next[idx], [field]: value };
    setProducts(next);
  };
  const remove = (idx) => setProducts(products.filter((_, i) => i !== idx));
  const add = () => setProducts([...products, emptyProduct()]);

  return (
    <div className="crm-product-table">
      <div className="crm-product-table-head">
        <span>Product</span><span>Qty</span><span>Rate (₹)</span><span>GST %</span><span>Total</span><span></span>
      </div>
      {products.map((p, idx) => (
        <div className="crm-product-row" key={idx}>
          <div className="crm-product-name-cell">
            <input
              type="text" placeholder="Product name" value={p.name}
              onChange={(e) => update(idx, 'name', e.target.value)}
            />
            <textarea
              placeholder="Description (size, paper, finish, etc.)" value={p.description}
              onChange={(e) => update(idx, 'description', e.target.value)} rows={2}
            />
          </div>
          <input type="number" min="0" value={p.qty} onChange={(e) => update(idx, 'qty', e.target.value)} />
          <input type="number" min="0" value={p.rate} onChange={(e) => update(idx, 'rate', e.target.value)} />
          <input type="number" min="0" value={p.gstPercent} onChange={(e) => update(idx, 'gstPercent', e.target.value)} />
          <div className="crm-line-total">{money(lineTotal(p))}</div>
          <button className="crm-icon-btn crm-icon-btn--danger" onClick={() => remove(idx)} title="Remove product">
            <FiTrash2 size={15} />
          </button>
        </div>
      ))}
      <button className="crm-btn crm-btn--ghost" onClick={add}>
        <FiPlus size={15} /> Add Product
      </button>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
   Shared Quotation Form (used for New + Edit)
   ──────────────────────────────────────────────────────────────────── */

const QuotationForm = ({ initial, onSave, saving, submitLabel }) => {
  const [customer, setCustomer] = useState(initial?.customer || emptyCustomer());
  const [products, setProducts] = useState(Array.isArray(initial?.products) && initial.products.length ? initial.products : [emptyProduct()]);
  const [searchQuery, setSearchQuery] = useState(customer.customerPhone || customer.companyName || '');
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showMatches, setShowMatches] = useState(false);
  const debounceRef = useRef(null);

  const grandTotal = useMemo(() => grandTotalOf(products), [products]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!searchQuery || searchQuery.trim().length < 3) {
      setMatches([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        setSearching(true);
        const { data } = await API.get(`/crm/lookup?q=${encodeURIComponent(searchQuery.trim())}`);
        setMatches(data.matches || []);
        setShowMatches(true);
      } catch {
        // silent — lookup is a convenience, not critical
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  const applyMatch = (m) => {
    setCustomer({
      customerName: m.customerName || '',
      customerPhone: m.customerPhone || '',
      customerEmail: m.customerEmail || '',
      companyName: m.companyName || '',
      gstNo: m.gstNo || '',
      companyAddress: m.companyAddress || '',
    });
    setShowMatches(false);
    toast.success('Details auto-filled from registered records');
  };

  const handleField = (field, value) => setCustomer(c => ({ ...c, [field]: value }));

  const handleSubmit = () => {
    if (!customer.customerName.trim() || !customer.customerPhone.trim()) {
      toast.error('Name and phone number are required');
      return;
    }
    if (!products.length || products.some(p => !p.name.trim())) {
      toast.error('Add at least one product with a name');
      return;
    }
    onSave({ ...customer, products, grandTotal });
  };

  return (
    <div className="crm-card">
      <div className="crm-search-wrap">
        <label className="crm-field-label">Search by phone number or company name</label>
        <div className="crm-search-input">
          <FiSearch size={16} />
          <input
            type="text"
            placeholder="e.g. 9876543210 or Angel Traders Pvt Ltd"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setShowMatches(true); }}
            onFocus={() => setShowMatches(true)}
          />
          {searching && <span className="crm-spinner" />}
        </div>
        {showMatches && matches.length > 0 && (
          <div className="crm-match-list">
            {matches.map((m, i) => (
              <button key={i} className="crm-match-item" onClick={() => applyMatch(m)}>
                <FiUser size={14} />
                <div>
                  <div className="crm-quote-strong">{m.customerName} · {m.customerPhone}</div>
                  <div className="crm-quote-muted">{m.companyName || 'Individual customer'}</div>
                </div>
                <span className="crm-match-tag">Registered</span>
              </button>
            ))}
          </div>
        )}
        {showMatches && searchQuery.trim().length >= 3 && !searching && matches.length === 0 && (
          <div className="crm-match-empty">No registered match — fill details below manually.</div>
        )}
      </div>

      <div className="crm-form-grid">
        <div className="crm-field">
          <label className="crm-field-label"><FiUser size={13} /> Customer Name *</label>
          <input value={customer.customerName} onChange={(e) => handleField('customerName', e.target.value)} placeholder="Full name" />
        </div>
        <div className="crm-field">
          <label className="crm-field-label"><FiPhone size={13} /> Phone Number *</label>
          <input value={customer.customerPhone} onChange={(e) => handleField('customerPhone', e.target.value)} placeholder="10-digit mobile" />
        </div>
        <div className="crm-field">
          <label className="crm-field-label"><FiMail size={13} /> Email</label>
          <input value={customer.customerEmail} onChange={(e) => handleField('customerEmail', e.target.value)} placeholder="name@company.com" />
        </div>
        <div className="crm-field">
          <label className="crm-field-label"><FiBriefcase size={13} /> Company Name</label>
          <input value={customer.companyName} onChange={(e) => handleField('companyName', e.target.value)} placeholder="Optional" />
        </div>
        <div className="crm-field">
          <label className="crm-field-label">GST Number</label>
          <input value={customer.gstNo} onChange={(e) => handleField('gstNo', e.target.value)} placeholder="Optional" />
        </div>
        <div className="crm-field crm-field--wide">
          <label className="crm-field-label">Company Address</label>
          <input value={customer.companyAddress} onChange={(e) => handleField('companyAddress', e.target.value)} placeholder="Optional" />
        </div>
      </div>

      <h4 className="crm-section-title">Products</h4>
      <ProductTable products={products} setProducts={setProducts} />

      <div className="crm-grand-total-bar">
        <span>Grand Total</span>
        <span className="crm-grand-total-amount">{money(grandTotal)}</span>
      </div>

      <div className="crm-form-actions">
        <button className="crm-btn crm-btn--primary crm-btn--lg" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving…' : (submitLabel || 'Save Quotation')}
        </button>
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
   Follow-up Section
   ──────────────────────────────────────────────────────────────────── */

const emptyFollowup = () => ({
  date: new Date().toISOString().slice(0, 10),
  channel: 'call',
  note: '',
  outcome: 'interested',
  nextFollowupDate: '',
});

const FollowupSection = ({ quotationId, logs, nextFollowupDate, onUpdate }) => {
  const { log } = useStaffLog();
  const [form, setForm] = useState(emptyFollowup());
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const isOverdue = useMemo(() => {
    if (!nextFollowupDate) return false;
    return new Date(nextFollowupDate) < new Date(new Date().toISOString().slice(0, 10));
  }, [nextFollowupDate]);

  const handleField = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleAdd = async () => {
    if (!form.note.trim()) { toast.error('Please write what happened'); return; }
    try {
      setSaving(true);
      const payload = {
        date: form.date,
        channel: form.channel,
        note: form.note.trim(),
        outcome: form.outcome,
        nextFollowupDate: form.nextFollowupDate || null,
      };
      const { data } = await API.post(`/crm/${quotationId}/followup`, payload);
      onUpdate(data.quotation);
      setForm(emptyFollowup());
      toast.success('Follow-up logged');
      await log('logged_followup', `Logged follow-up on quotation #${quotationId}`, 'CrmQuotation', quotationId);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save follow-up');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Remove this follow-up entry?')) return;
    try {
      setDeleting(entryId);
      const { data } = await API.delete(`/crm/${quotationId}/followup/${entryId}`);
      onUpdate(data.quotation);
      toast.success('Entry removed');
    } catch {
      toast.error('Failed to remove entry');
    } finally {
      setDeleting(null);
    }
  };

  const sortedLogs = useMemo(() =>
    [...(logs || [])].sort((a, b) => new Date(b.date) - new Date(a.date)),
    [logs]
  );

  return (
    <div className="crm-card crm-followup-section">
      <h4 className="crm-section-title" style={{ marginTop: 0 }}>Follow-up Log</h4>
      {isOverdue && (
        <div className="crm-followup-reminder">
          <FiCalendar size={14} />
          Follow-up was due on <strong>{new Date(nextFollowupDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</strong> — log an update below.
        </div>
      )}
      {nextFollowupDate && !isOverdue && (
        <div className="crm-followup-scheduled">
          <FiClock size={13} />
          Next follow-up scheduled: <strong>{new Date(nextFollowupDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</strong>
        </div>
      )}
      <div className="crm-followup-form">
        <div className="crm-followup-form-row">
          <div className="crm-field">
            <label className="crm-field-label">Date</label>
            <input type="date" value={form.date} onChange={(e) => handleField('date', e.target.value)} />
          </div>
          <div className="crm-field">
            <label className="crm-field-label">Channel</label>
            <select value={form.channel} onChange={(e) => handleField('channel', e.target.value)}>
              <option value="call">📞 Call</option>
              <option value="whatsapp">💬 WhatsApp</option>
              <option value="email">✉️ Email</option>
              <option value="visit">🏠 Visit</option>
            </select>
          </div>
          <div className="crm-field">
            <label className="crm-field-label">Outcome</label>
            <select value={form.outcome} onChange={(e) => handleField('outcome', e.target.value)}>
              <option value="interested">Interested</option>
              <option value="needs_time">Needs Time</option>
              <option value="not_reachable">Not Reachable</option>
              <option value="rejected">Rejected</option>
              <option value="converted">Converted</option>
            </select>
          </div>
          <div className="crm-field">
            <label className="crm-field-label">Next Follow-up</label>
            <input type="date" value={form.nextFollowupDate} onChange={(e) => handleField('nextFollowupDate', e.target.value)} placeholder="Optional" />
          </div>
        </div>
        <div className="crm-followup-note-row">
          <textarea
            className="crm-followup-note"
            rows={2}
            placeholder="What happened? e.g. Customer said price is high, will decide by Friday."
            value={form.note}
            onChange={(e) => handleField('note', e.target.value)}
          />
          <button className="crm-btn crm-btn--primary" onClick={handleAdd} disabled={saving}>
            <FiPlus size={15} /> {saving ? 'Saving…' : 'Log'}
          </button>
        </div>
      </div>
      {sortedLogs.length === 0 ? (
        <p className="crm-followup-empty">No follow-ups logged yet.</p>
      ) : (
        <div className="crm-followup-timeline">
          {sortedLogs.map((entry) => (
            <div key={entry.id} className="crm-followup-entry">
              <div className="crm-followup-entry-meta">
                <span className="crm-followup-date">
                  {new Date(entry.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
                <span className="crm-followup-channel">{CHANNEL_LABELS[entry.channel] || entry.channel}</span>
                <OutcomeBadge outcome={entry.outcome} />
                {entry.addedBy && <span className="crm-quote-muted">by {entry.addedBy}</span>}
                <button
                  className="crm-icon-btn crm-icon-btn--danger crm-followup-del"
                  onClick={() => handleDelete(entry.id)}
                  disabled={deleting === entry.id}
                  title="Remove entry"
                >
                  <FiTrash2 size={13} />
                </button>
              </div>
              <p className="crm-followup-note-text">{entry.note}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
   Confirmed Banner
   ──────────────────────────────────────────────────────────────────── */

const ConfirmedBanner = () => (
  <div className="crm-order-banner">
    <div className="crm-order-banner-left">
      <FiPackage size={20} />
      <div>
        <div className="crm-order-banner-title">This quotation is confirmed</div>
        <div className="crm-order-banner-sub">Order has been accepted by the customer</div>
      </div>
    </div>
  </div>
);

/* ────────────────────────────────────────────────────────────────────
   New Quotation page
   ──────────────────────────────────────────────────────────────────── */

const NewQuotationPage = () => {
  const navigate = useNavigate();
  const { log } = useStaffLog();
  const [saving, setSaving] = useState(false);

  const handleSave = async (payload) => {
    try {
      setSaving(true);
      const { data } = await API.post('/crm', payload);
      toast.success('Quotation created');
      await log('created_quotation', `Created quotation #${data.quotation.quotationNumber}`, 'CrmQuotation', data.quotation.id);
      navigate(`/admin/quotations/${data.quotation.id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save quotation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="crm-page">
      <div className="crm-page-head">
        <Link to="/admin/quotations" className="crm-back-link"><FiArrowLeft size={16} /> Back to quotations</Link>
        <h2>New Quotation</h2>
        <p className="crm-page-sub">Search an existing customer or fill in fresh details, then add products below.</p>
      </div>
      <QuotationForm onSave={handleSave} saving={saving} submitLabel="Save Quotation" />
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
   Quotation Detail page
   ──────────────────────────────────────────────────────────────────── */

const QuotationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { log } = useStaffLog();
  const [quotation, setQuotation] = useState(null);
  const [chain, setChain] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [negotiating, setNegotiating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await API.get(`/crm/${id}`);
      setQuotation(data.quotation);
      setChain(data.chain || []);
    } catch (err) {
      toast.error('Could not load quotation');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { setEditing(false); load(); }, [id, load]);

  const { labels } = useMemo(() => buildChainLabels(chain.length ? chain : (quotation ? [quotation] : [])), [chain, quotation]);
  const label = quotation ? (labels[quotation.id] || 'Q?') : '';

  const isLatestInChain = useMemo(() => {
    if (!quotation || !chain.length) return true;
    const maxRound = Math.max(...chain.map(c => c.roundNumber));
    return quotation.roundNumber === maxRound;
  }, [chain, quotation]);

  const handleEditSave = async (payload) => {
    try {
      setSaving(true);
      const { data } = await API.put(`/crm/${id}`, payload);
      setQuotation(data.quotation);
      setEditing(false);
      toast.success('Quotation updated');
      await log('updated_quotation', `Updated quotation #${data.quotation.quotationNumber}`, 'CrmQuotation', data.quotation.id);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleNegotiate = async () => {
    try {
      setNegotiating(true);
      const { data } = await API.post(`/crm/${id}/negotiate`);
      toast.success('New negotiation round created');
      navigate(`/admin/quotations/${data.quotation.id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start negotiation');
    } finally {
      setNegotiating(false);
    }
  };

  const handleConfirm = async () => {
    try {
      setConfirming(true);
      const { data: qData } = await API.put(`/crm/${id}`, { status: 'confirmed' });
      setQuotation(qData.quotation);
      toast.success('Quotation confirmed');
      await log('confirmed_quotation', `Confirmed quotation #${quotation?.quotationNumber}`, 'CrmQuotation', Number(id));
      setShowPreview(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to confirm');
    } finally {
      setConfirming(false);
    }
  };

  if (loading) return <div className="crm-page"><div className="crm-loading">Loading quotation…</div></div>;
  if (!quotation) return <div className="crm-page"><div className="crm-loading">Quotation not found.</div></div>;

  // Show follow-up section for any status except draft
  const showFollowup = quotation.status !== 'draft';

  return (
    <div className="crm-page">
      <div className="crm-page-head">
        <Link to="/admin/quotations" className="crm-back-link"><FiArrowLeft size={16} /> Back to quotations</Link>
        <div className="crm-detail-title-row">
          <h2>{label} <span className="crm-quote-muted">— {quotation.customerName}</span></h2>
          <StatusBadge status={quotation.status} />
        </div>
      </div>

      {chain.length > 1 && (
        <div className="crm-chain-strip">
          {chain.sort((a, b) => a.roundNumber - b.roundNumber).map((c, i, arr) => (
            <React.Fragment key={c.id}>
              <Link
                to={`/admin/quotations/${c.id}`}
                className={`crm-chain-pill ${c.id === quotation.id ? 'crm-chain-pill--active' : ''}`}
              >
                {labels[c.id]} <StatusBadge status={c.status} />
              </Link>
              {i < arr.length - 1 && <FiChevronRight className="crm-chain-arrow" />}
            </React.Fragment>
          ))}
        </div>
      )}

      {editing ? (
        <QuotationForm
          initial={{
            customer: {
              customerName: quotation.customerName,
              customerPhone: quotation.customerPhone,
              customerEmail: quotation.customerEmail || '',
              companyName: quotation.companyName || '',
              gstNo: quotation.gstNo || '',
              companyAddress: quotation.companyAddress || '',
            },
            products: Array.isArray(quotation.products) ? quotation.products : [],
          }}
          onSave={handleEditSave}
          saving={saving}
          submitLabel="Save Changes"
        />
      ) : (
        <div className="crm-card">
          <div className="crm-form-grid crm-form-grid--readonly">
            <div><span className="crm-field-label">Customer</span><div className="crm-quote-strong">{quotation.customerName}</div></div>
            <div><span className="crm-field-label">Phone</span><div className="crm-quote-strong">{quotation.customerPhone}</div></div>
            <div><span className="crm-field-label">Email</span><div className="crm-quote-strong">{quotation.customerEmail || '—'}</div></div>
            <div><span className="crm-field-label">Company</span><div className="crm-quote-strong">{quotation.companyName || '—'}</div></div>
            <div><span className="crm-field-label">GST No.</span><div className="crm-quote-strong">{quotation.gstNo || '—'}</div></div>
            <div className="crm-field--wide"><span className="crm-field-label">Address</span><div className="crm-quote-strong">{quotation.companyAddress || '—'}</div></div>
          </div>

          <h4 className="crm-section-title">Products</h4>
          <table className="crm-quote-preview-table">
            <thead><tr><th>Product</th><th>Qty</th><th>Rate</th><th>GST</th><th>Total</th></tr></thead>
            <tbody>
              {(Array.isArray(quotation.products) ? quotation.products : []).map((p, i) => (
                <tr key={i}>
                  <td>
                    <div className="crm-quote-strong">{p.name}</div>
                    {(p.description || p.quality) && <div className="crm-quote-muted">{p.description || p.quality}</div>}
                  </td>
                  <td>{p.qty}</td>
                  <td>{money(p.rate)}</td>
                  <td>{p.gstPercent}%</td>
                  <td>{money(lineTotal(p))}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="crm-grand-total-bar">
            <span>Grand Total</span>
            <span className="crm-grand-total-amount">{money(quotation.grandTotal)}</span>
          </div>

          {isLatestInChain ? (
            <div className="crm-detail-actions">
              <button className="crm-btn crm-btn--outline" onClick={() => setEditing(true)}>
                <FiEdit2 size={15} /> Edit
              </button>
              <button className="crm-btn crm-btn--outline" onClick={handleNegotiate} disabled={negotiating}>
                <FiMessageCircle size={15} /> {negotiating ? 'Creating…' : 'Negotiate'}
              </button>
              <button className="crm-btn crm-btn--primary" onClick={() => setShowPreview(true)}>
                <FiPrinter size={15} /> Confirm
              </button>
            </div>
          ) : (
            <div className="crm-detail-actions">
              <span className="crm-quote-muted">Superseded by a newer negotiation round — see {labels[chain[chain.length - 1]?.id]} above.</span>
            </div>
          )}
        </div>
      )}

      {/* ── Follow-up section (shown once quotation is sent/beyond) ── */}
      {showFollowup && !editing && (
        <FollowupSection
          quotationId={quotation.id}
          logs={quotation.followupLogs || []}
          nextFollowupDate={quotation.nextFollowupDate}
          onUpdate={(updated) => setQuotation(updated)}
        />
      )}

      {/* ── Confirmed banner — link to dedicated Order page ── */}
      {quotation.status === 'confirmed' && !editing && (
        <ConfirmedBanner quotationId={quotation.id} />
      )}

      {showPreview && (
        <PreviewModal
          quotation={quotation}
          label={label}
          onClose={() => setShowPreview(false)}
          onConfirm={quotation.status !== 'confirmed' ? handleConfirm : null}
          confirming={confirming}
        />
      )}
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
   Quotation List page
   ──────────────────────────────────────────────────────────────────── */

const QuotationListPage = () => {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/crm');
      setQuotations(data.quotations || []);
    } catch {
      toast.error('Could not load quotations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const { groups } = useMemo(() => buildChainLabels(quotations), [quotations]);

  const filteredGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return groups;
    return groups.filter(g => g.items.some(q =>
      q.customerName?.toLowerCase().includes(term) ||
      q.customerPhone?.includes(term) ||
      q.companyName?.toLowerCase().includes(term)
    ));
  }, [groups, search]);

  const toggle = (rootIndex) => setExpanded(e => ({ ...e, [rootIndex]: !e[rootIndex] }));

  return (
    <div className="crm-page">
      <div className="crm-page-head crm-page-head--row">
        <div>
          <h2>Quotations</h2>
          <p className="crm-page-sub">Every quotation and its negotiation history, newest customers first.</p>
        </div>
        <Link to="/admin/quotations/new" className="crm-btn crm-btn--primary crm-btn--lg">
          <FiPlus size={16} /> New Quotation
        </Link>
      </div>

      <div className="crm-search-input crm-search-input--bare">
        <FiSearch size={16} />
        <input
          placeholder="Search by customer name, phone, or company…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="crm-loading">Loading quotations…</div>
      ) : filteredGroups.length === 0 ? (
        <div className="crm-empty">
          <p>No quotations yet.</p>
          <Link to="/admin/quotations/new" className="crm-btn crm-btn--primary">
            <FiPlus size={15} /> Create your first quotation
          </Link>
        </div>
      ) : (
        <div className="crm-group-list">
          {[...filteredGroups].reverse().map((g) => {
            const root = g.items[0];
            const latest = g.items[g.items.length - 1];
            const isOpen = expanded[g.rootIndex] ?? false;
            return (
              <div className="crm-group-card" key={g.rootIndex}>
                <button className="crm-group-head" onClick={() => g.items.length > 1 && toggle(g.rootIndex)}>
                  <div className="crm-group-head-left">
                    <span className="crm-group-id">Q{g.rootNum}</span>
                    <div>
                      <div className="crm-quote-strong">{root.customerName}</div>
                      <div className="crm-quote-muted">{root.companyName || root.customerPhone}</div>
                    </div>
                  </div>
                  <div className="crm-group-head-right">
                    {g.items.length > 1 && <span className="crm-round-count"><FiClock size={12} /> {g.items.length} rounds</span>}
                    <span className="crm-grand-total-amount">{money(latest.grandTotal)}</span>
                    <StatusBadge status={latest.status} />
                    {g.items.length > 1 && (
                      <FiChevronRight className={`crm-group-caret ${isOpen ? 'crm-group-caret--open' : ''}`} />
                    )}
                  </div>
                </button>

                {(isOpen || g.items.length === 1) && (
                  <div className="crm-group-body">
                    {[...g.items].reverse().map((q) => (
                      <Link to={`/admin/quotations/${q.id}`} key={q.id} className="crm-quote-row">
                        <span className="crm-quote-row-label">Q{g.rootNum}{q.roundNumber > 0 ? `.${q.roundNumber}` : ''}</span>
                        <span className="crm-quote-muted">{new Date(q.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                        <span className="crm-grand-total-amount">{money(q.grandTotal)}</span>
                        <StatusBadge status={q.status} />
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
   Root export — mounted at /admin/crm/*
   ──────────────────────────────────────────────────────────────────── */

const AdminCRM = () => (
  <Routes>
    <Route index element={<QuotationListPage />} />
    <Route path="new" element={<NewQuotationPage />} />
    <Route path=":id" element={<QuotationDetailPage />} />
  </Routes>
);

export default AdminCRM;